/**
 * PanelGear API Service
 * 
 * This module provides high-level API functions for the admin app.
 * All operations use direct Appwrite SDK calls with team-based permissions.
 * 
 * Replaces the old dataApi.js that routed through the data-api function.
 * 
 * @version 2.0.0 - Direct SDK calls with Teams
 */

import appwrite, {
  databases,
  storage,
  teams,
  DATABASE_ID,
  COLLECTIONS,
  BUCKETS,
  FUNCTION_IDS,
  Query,
  ID,
  Permission,
  Role,
  getCurrentUser,
  getFilePreview
} from './appwrite';
import { withReference } from '../utils/ticket-reference';
import { softDelete } from './records';
import { notDeleted } from '../lib/records';
import { countByStatus, statusFilterValues } from '../lib/quoteRequestStatus';


// =====================================================
// SHOP LINE SHEET
// =====================================================
//
// The lines the insurer guides do not carry but shops still bill — a cant rail,
// a boot glass trim, an arch moulding clip. Two kinds of row live in one table:
//
//   platform standard   organisationId blank. Every organisation gets these,
//                       including ones that already exist, with nothing to
//                       configure. Seeded; read-only to a shop.
//   the shop's own      organisationId set. Either a line the shop added, or its
//                       edit or disabling of a standard line, which names the
//                       standard row in `standardLineId`.
//
// A shop never edits the shared standard row — it belongs to every other shop
// too. Editing one writes an override; disabling one writes an inactive
// override. Both are undone by deleting the override, which is why "reset to
// standard" is a delete rather than a second edit.
//
// The three-layer resolution that decides which of these a quote actually sees
// lives server-side in functions/ai-damage-assessment-orchestrator/
// org-lines-core.js. This module reads and writes rows; it does not resolve
// them, because a catalogue resolved two ways is a catalogue resolved wrongly.

// Both catalogues the part lookup reads are whole-collection paginations, and
// the damage screen mounts one lookup per part row plus one per section — 54 of
// them on a measured assessment of 48 parts across 6 sections. Uncached, that
// asked for this entire sheet 54 times on opening the quote, so what a quote
// cost to open scaled with its line count; and again every time a drag moved a
// row into another section, because the row remounts there and its fresh lookup
// refetches. Neither catalogue changes while a quote is open, so read each once
// per session and hand every lookup the same rows. Writes below drop the cache.
//
// Keyed by organisation: selecting another shop does not reload the page, so a
// cache that ignored the team would answer with the previous shop's sheet.
const shopLineCache = new Map(); // teamId -> Promise<rows[]>

/** Forget the cached line sheet, so the next read sees a write. */
export function clearShopLineCache() {
  shopLineCache.clear();
}

/** Every row this organisation can see: the standards, plus its own. */
export async function shopLineList() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');
  const key = user.teamId;
  if (!shopLineCache.has(key)) {
    shopLineCache.set(
      key,
      shopLineListUncached(user).catch((err) => {
        shopLineCache.delete(key); // a failed read must not be remembered
        throw err;
      }),
    );
  }
  // A copy per caller: the settings screen sorts what it gets back in place,
  // and that must not reorder the array the part lookup is reading.
  return (await shopLineCache.get(key)).slice();
}

async function shopLineListUncached(user) {
  // Row security means the query cannot return another shop's rows, so there is
  // nothing to filter on: what comes back is the standards plus this shop's.
  const documents = [];
  let offset = 0;
  for (;;) {
    const page = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.ORG_LINE_ALLOWANCES,
      [Query.limit(100), Query.offset(offset)]
    );
    documents.push(...page.documents);
    if (page.documents.length < 100) break;
    offset += 100;
  }

  // A deleted line or override is still returned until it is purged. Dropped
  // here, before `overrideFor` is built — otherwise a deleted override would
  // still show as the line's edit. functions/.../org-lines-core.js does the same
  // for pricing, so what this screen shows and what a quote prices agree.
  const live = documents.filter((row) => !row.deletedAt);
  const standards = live.filter((row) => !String(row.organisationId || '').trim());
  const mine = live.filter((row) => String(row.organisationId || '').trim() === user.teamId);
  const overrideFor = new Map(
    mine.filter((row) => row.standardLineId).map((row) => [row.standardLineId, row])
  );

  // Presented the way the screen reads it: one entry per line the shop bills,
  // each knowing whether it is a standard, an edited standard, or the shop's
  // own. The override is carried alongside rather than merged in, so the form
  // can show what the standard says underneath what the shop changed it to.
  return [
    ...standards.map((row) => ({
      ...row,
      kind: overrideFor.has(row.$id) ? 'edited' : 'standard',
      override: overrideFor.get(row.$id) || null,
      disabled: overrideFor.get(row.$id)?.active === false,
    })),
    ...mine
      .filter((row) => !row.standardLineId)
      .map((row) => ({ ...row, kind: 'own', override: null, disabled: row.active === false })),
  ];
}

/** The fields a shop may set on a line, and nothing else. */
const SHOP_LINE_FIELDS = [
  'lineName', 'searchTerms', 'quoteCategory', 'notes',
  'removeRefitHoursStandard', 'repairHoursStandard', 'paintUnits',
];
const SHOP_LINE_NUMBERS = ['removeRefitHoursStandard', 'repairHoursStandard', 'paintUnits'];
/** Stored as JSON strings, because Appwrite holds them as strings. */
const SHOP_LINE_LISTS = ['insurerConfigIds', 'locations'];

/** A stored list column, as an array. Mirrors parseList in org-lines-core.js. */
export function shopLineList_parse(value) {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  const raw = String(value || '').trim();
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
  } catch {
    return raw.split(',').map((v) => v.trim()).filter(Boolean);
  }
}

/**
 * The one section a line reports as its own.
 *
 * One section is that section; several is deliberately none. primaryLocation
 * overrides a damage item's zone downstream, so a line spanning two corners has
 * no business dragging an item to whichever sorted first. Mirrors
 * primaryLocationFor in org-lines-core.js — the two must agree, or a line reads
 * one way in the settings screen and another way on a quote.
 */
function primaryLocationFor(locations) {
  const zones = shopLineList_parse(locations);
  return zones.length === 1 ? zones[0] : '';
}

function shopLinePayload(values) {
  const out = {};
  for (const key of SHOP_LINE_FIELDS) {
    if (!(key in values)) continue;
    const value = values[key];
    if (SHOP_LINE_NUMBERS.includes(key)) {
      out[key] = value === '' || value == null ? null : Number(value);
    } else {
      out[key] = value ?? '';
    }
  }
  for (const key of SHOP_LINE_LISTS) {
    if (!(key in values)) continue;
    out[key] = JSON.stringify(shopLineList_parse(values[key]));
  }
  // The sections decide the primary, so it is never set by hand — two fields
  // that can disagree about the same fact is how they come to disagree.
  if ('locations' in values) out.primaryLocation = primaryLocationFor(values.locations);
  // Private work is not an insurer with an empty id; it is its own answer, and
  // it defaults to yes because the shop's rates are the only rates on such a job.
  if ('appliesToPrivate' in values) out.appliesToPrivate = values.appliesToPrivate !== false;
  // A part only an electric vehicle has, so the assessment offers it on nothing
  // else. Defaults to no: a line the shop adds is offered on every vehicle.
  if ('electricVehicleOnly' in values) out.electricVehicleOnly = values.electricVehicleOnly === true;
  return out;
}

/** Permissions for a row this organisation owns. Nobody else can read it. */
function shopLinePermissions(teamId) {
  return [
    Permission.read(Role.team(teamId)),
    Permission.update(Role.team(teamId)),
    Permission.delete(Role.team(teamId)),
  ];
}

/** Add a line of the shop's own, looked up from then on like a standard one. */
export async function shopLineCreate(values) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');
  if (!String(values.lineName || '').trim()) throw new Error('A line needs a name');

  const created = await databases.createDocument(
    DATABASE_ID,
    COLLECTIONS.ORG_LINE_ALLOWANCES,
    ID.unique(),
    {
      // A new line shows everywhere until the shop narrows it: every insurer
      // (an empty list) and private work. Anything else would mean a line the
      // shop just added silently not appearing on the next job.
      insurerConfigIds: '[]',
      appliesToPrivate: true,
      locations: '[]',
      primaryLocation: '',
      ...shopLinePayload(values),
      organisationId: user.teamId,
      standardLineId: '',
      active: values.active !== false,
    },
    shopLinePermissions(user.teamId)
  );
  clearShopLineCache();
  return created;
}

/**
 * Change a standard line for this organisation only.
 *
 * Writes the shop's own override row rather than touching the standard, which
 * every other organisation reads. Called again on an already-overridden line, it
 * updates that override.
 *
 * An override is a PARTIAL: it names the standard it modifies and carries only
 * the fields the shop actually changed. That is why lineName is not a required
 * column on this table even though every line has a name — switching a standard
 * line off writes an override that says nothing but `active: false`, and forcing
 * it to repeat the name would pin that name if the standard's were ever
 * improved. A line the shop adds itself does need one, which shopLineCreate
 * enforces.
 */
export async function shopLineOverride(standardLine, values) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const payload = {
    ...shopLinePayload(values),
    organisationId: user.teamId,
    standardLineId: standardLine.$id,
    active: values.active !== false,
  };

  const written = standardLine.override
    ? await databases.updateDocument(
        DATABASE_ID, COLLECTIONS.ORG_LINE_ALLOWANCES, standardLine.override.$id, payload
      )
    : await databases.createDocument(
        DATABASE_ID, COLLECTIONS.ORG_LINE_ALLOWANCES, ID.unique(), payload,
        shopLinePermissions(user.teamId)
      );
  clearShopLineCache();
  return written;
}

/** Edit a line the shop added itself. */
export async function shopLineUpdate(line, values) {
  const updated = await databases.updateDocument(
    DATABASE_ID, COLLECTIONS.ORG_LINE_ALLOWANCES, line.$id,
    {
      ...shopLinePayload(values),
      ...(values.active !== undefined ? { active: values.active !== false } : {}),
    }
  );
  clearShopLineCache();
  return updated;
}

/**
 * Switch a line off, or back on, for this organisation.
 *
 * A standard line is disabled by an inactive override; the standard row itself
 * is untouched and every other shop keeps it.
 */
export async function shopLineSetActive(line, active) {
  if (line.kind === 'own') return shopLineUpdate(line, { active });
  return shopLineOverride(line, { active });
}

/**
 * Drop the shop's changes to a standard line and go back to the standard.
 *
 * A delete rather than a second edit, because "no override" is exactly what
 * "use the standard" means — leaving an override that happens to match would
 * silently pin the line if the standard were ever updated.
 */
export async function shopLineResetToStandard(line) {
  if (!line.override) return null;
  // Soft-deleted, so the shop's values can be restored; a deleted override is
  // ignored by the line sheet and by pricing alike.
  const removed = await softDelete(COLLECTIONS.ORG_LINE_ALLOWANCES, line.override);
  clearShopLineCache();
  return removed;
}

/**
 * Add several lines at once — what a paste out of a spreadsheet produces.
 *
 * Written one at a time rather than in a batch because Appwrite has no batch
 * create, and sequentially rather than in parallel so a paste of forty rows does
 * not open forty connections. Each result is reported individually: a paste
 * where three rows are missing a name should still write the other thirty-seven
 * and say which three it could not.
 */
export async function shopLineCreateMany(rowsToAdd) {
  const created = [];
  const failed = [];
  for (const values of rowsToAdd) {
    try {
      created.push(await shopLineCreate(values));
    } catch (err) {
      failed.push({ values, error: err.message });
    }
  }
  return { created, failed };
}

/** Remove a line the shop added. A standard line is disabled, never deleted. */
export async function shopLineDelete(line) {
  if (line.kind !== 'own') throw new Error('A standard line can be disabled, not deleted');
  const removed = await softDelete(COLLECTIONS.ORG_LINE_ALLOWANCES, line);
  clearShopLineCache();
  return removed;
}

// =====================================================
// QUOTE REQUESTS
// =====================================================

/**
 * Get quote request list with optional filters
 */
export async function quoteRequestList(filters = {}) {
  // Deleted requests are left out here, and so out of the dashboard counts and
  // global search, which both read through this.
  const queries = [notDeleted(), Query.orderDesc('$createdAt'), Query.limit(100)];
  
  // Add status filter if provided. Matching the legacy spellings too keeps the
  // list in step with the dashboard counts, which read them as the same thing.
  if (filters.status && filters.status !== 'all') {
    queries.push(Query.equal('status', statusFilterValues(filters.status)));
  }
  
  const response = await appwrite.listDocuments(COLLECTIONS.QUOTE_REQUESTS, queries);
  let documents = response.documents;
  
  // Batch fetch related customers and vehicles for enrichment
  const customerIds = [...new Set(documents.map(d => d.customerId).filter(Boolean))];
  const vehicleIds = [...new Set(documents.map(d => d.customerVehicleId).filter(Boolean))];
  
  const [customers, vehicles] = await Promise.all([
    customerIds.length > 0
      ? appwrite.listDocuments(COLLECTIONS.CUSTOMERS, [Query.equal('$id', customerIds), Query.limit(customerIds.length)]).then(r => r.documents).catch(() => [])
      : [],
    vehicleIds.length > 0
      ? appwrite.listDocuments(COLLECTIONS.CUSTOMER_VEHICLES, [Query.equal('$id', vehicleIds), Query.limit(vehicleIds.length)]).then(r => r.documents).catch(() => [])
      : []
  ]);
  
  const customerMap = Object.fromEntries(customers.map(c => [c.$id, c]));
  const vehicleMap = Object.fromEntries(vehicles.map(v => [v.$id, v]));
  
  documents = documents.map(doc => ({
    ...doc,
    customer: doc.customerId ? customerMap[doc.customerId] || null : null,
    vehicle: doc.customerVehicleId ? vehicleMap[doc.customerVehicleId] || null : null
  }));
  
  // Client-side search filter (Appwrite full-text search requires specific setup)
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    documents = documents.filter(doc => {
      const searchableFields = [
        doc.damageDescription,
        doc.notes,
        doc.customer?.firstName,
        doc.customer?.lastName,
        doc.customer?.email,
        doc.customer?.phone,
        doc.vehicle?.registration,
        doc.vehicle?.rego,
        doc.vehicle?.make,
        doc.vehicle?.model,
        doc.vehicleMake,
        doc.vehicleModel,
        doc.panelQuoteJobNumber?.toString()
      ];
      return searchableFields.some(field => 
        field && field.toLowerCase().includes(searchLower)
      );
    });
  }
  
  return documents;
}

/**
 * Get a single quote request with related data
 */
export async function quoteRequestGet(id) {
  const quoteRequest = await appwrite.getDocument(COLLECTIONS.QUOTE_REQUESTS, id);
  
  // Fetch related customer and vehicle data
  const [customer, vehicle] = await Promise.all([
    quoteRequest.customerId 
      ? appwrite.getDocument(COLLECTIONS.CUSTOMERS, quoteRequest.customerId).catch(() => null) 
      : null,
    quoteRequest.customerVehicleId 
      ? appwrite.getDocument(COLLECTIONS.CUSTOMER_VEHICLES, quoteRequest.customerVehicleId).catch(() => null) 
      : null
  ]);
  
  return {
    ...quoteRequest,
    customer,
    vehicle
  };
}

/**
 * Create a new quote request
 */
export async function quoteRequestCreate(data) {
  return await appwrite.createDocument(
    COLLECTIONS.QUOTE_REQUESTS,
    {
      ...data,
      status: data.status || 'new'
    }
  );
}

/**
 * Link an individual customer to a quote request (clears any business link).
 * A quote request is for one OR the other — a person or a business.
 */
export async function quoteRequestLinkCustomer(id, customerId) {
  return await quoteRequestUpdate(id, { customerId: customerId || '', customerOrganisation: null });
}

/**
 * Link a business (customer organisation) to a quote request (clears any
 * individual-customer link). Uses the customerOrganisation relationship column.
 */
export async function quoteRequestLinkOrganisation(id, orgId) {
  return await quoteRequestUpdate(id, { customerId: '', customerOrganisation: orgId || null });
}

/**
 * Update a quote request
 */
export async function quoteRequestUpdate(id, data) {
  return await appwrite.updateDocument(
    COLLECTIONS.QUOTE_REQUESTS,
    id,
    {
      ...data
    }
  );
}

/**
 * Delete a quote request. Soft: it disappears everywhere, including the
 * customer's intake link, and can be restored for 90 days. Its photos, notes,
 * quote and damage are not touched — they are only reachable through it.
 * Accepts the row (whose permissions decide) or its id.
 */
export async function quoteRequestDelete(quoteRequestOrId) {
  return await softDelete(COLLECTIONS.QUOTE_REQUESTS, quoteRequestOrId);
}

// =====================================================
// QUOTE REQUEST IMAGES
// =====================================================

/**
 * Get images for a quote request
 */
export async function quoteRequestImages(quoteRequestId) {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.QUOTE_REQUEST_IMAGES,
    [
      Query.equal('quoteRequestId', quoteRequestId),
      notDeleted(),
      Query.orderAsc('imageOrder')
    ]
  );
  
  // Add URL to each image record for display
  const imagesWithUrls = response.documents.map(doc => ({
    ...doc,
    id: doc.$id,
    url: getFilePreview(doc.storageBucket || BUCKETS.QUOTE_REQUEST_IMAGES, doc.storagePath),
    created_at: doc.$createdAt
  }));
  
  return imagesWithUrls;
}

/**
 * Get a single quote request image with latest metadata
 */
export async function quoteRequestImageGet(imageId) {
  const document = await databases.getDocument(
    DATABASE_ID,
    COLLECTIONS.QUOTE_REQUEST_IMAGES,
    imageId
  );

  return {
    ...document,
    id: document.$id,
    url: getFilePreview(document.storageBucket || BUCKETS.QUOTE_REQUEST_IMAGES, document.storagePath),
    created_at: document.$createdAt
  };
}

/**
 * Upload an image for a quote request
 */
export async function uploadQuoteImage(quoteRequestId, file, metadata = {}) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  // Upload file to storage
  const fileId = ID.unique();
  await storage.createFile(
    BUCKETS.QUOTE_REQUEST_IMAGES,
    fileId,
    file,
    [
      // Scope to the uploader's org team. Only roles the uploader actually
      // holds may be granted (Appwrite blocks privilege escalation on create):
      // every member holds Role.team(teamId), but the `admin` team role is
      // never assigned to members, so granting it here fails the upload.
      Permission.read(Role.team(user.teamId)),
      Permission.update(Role.team(user.teamId)),
      Permission.delete(Role.team(user.teamId))
    ]
  );

  // Create image record
  const imageRecord = await appwrite.createDocument(
    COLLECTIONS.QUOTE_REQUEST_IMAGES,
    {
      quoteRequestId,
      storagePath: fileId,
      filename: file.name,
      fileSize: file.size,
      mimeType: file.type,
      storageBucket: BUCKETS.QUOTE_REQUEST_IMAGES,
      imageCategory: metadata.category || 'extra',
      imageName: metadata.name || file.name,
      imageOrder: metadata.order || 0
    }
  );

  return imageRecord;
}

/**
 * Delete a quote request image. Soft: the record is hidden and the storage file
 * kept, so a restore brings the photo back; cleanup-deleted-records removes both
 * 90 days later.
 */
export async function deleteQuoteRequestImage(image) {
  await softDelete(COLLECTIONS.QUOTE_REQUEST_IMAGES, image);
  return true;
}

/**
 * Save photos' new positions (and, for a photo dragged into another group, its
 * category). Takes the `[{ id, data }]` list from photoChanges() in
 * lib/photoGroups, which works out what moved by comparing against the photos
 * as they were — so this writes exactly the rows that changed.
 */
export async function saveQuoteRequestImagePositions(changes) {
  await Promise.all(
    changes.map(({ id, data }) =>
      databases.updateDocument(DATABASE_ID, COLLECTIONS.QUOTE_REQUEST_IMAGES, id, data)
    )
  );
  return true;
}

/**
 * Get image URL for preview
 */
export function getImageUrl(storagePath, width = null, height = null) {
  return appwrite.getFilePreview(BUCKETS.QUOTE_REQUEST_IMAGES, storagePath, width, height);
}

/**
 * URL that serves a quote request file as stored — how an attached PDF is
 * opened, since /preview only rasterises images.
 */
export function getImageFileUrl(storagePath) {
  return appwrite.getFileViewUrl(BUCKETS.QUOTE_REQUEST_IMAGES, storagePath);
}

/**
 * Update quote request image metadata (tags, damage boxes, etc.)
 */
export async function updateQuoteRequestImage(imageId, data) {
  const updateData = {
    ...data
  };
  
  return await databases.updateDocument(
    DATABASE_ID,
    COLLECTIONS.QUOTE_REQUEST_IMAGES,
    imageId,
    updateData
  );
}

// =====================================================
// CUSTOMERS
// =====================================================

/**
 * Get customer list
 */
export async function customerList(filters = {}) {
  const queries = [Query.orderDesc('$createdAt')];
  
  const response = await appwrite.listDocuments(COLLECTIONS.CUSTOMERS, queries);
  let documents = response.documents;
  
  // Client-side search filter
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    documents = documents.filter(doc => {
      const searchableFields = [
        doc.firstName,
        doc.lastName,
        doc.email,
        doc.phone
      ];
      return searchableFields.some(field => 
        field && field.toLowerCase().includes(searchLower)
      );
    });
  }
  
  return documents;
}

/**
 * Get a single customer
 */
export async function customerGet(id) {
  return await appwrite.getDocument(COLLECTIONS.CUSTOMERS, id);
}

/**
 * Create a new customer
 */
export async function customerCreate(data) {
  return await appwrite.createDocument(
    COLLECTIONS.CUSTOMERS,
    {
      ...data
    }
  );
}

/**
 * Update a customer
 */
export async function customerUpdate(id, data) {
  return await appwrite.updateDocument(
    COLLECTIONS.CUSTOMERS,
    id,
    {
      ...data
    }
  );
}

/**
 * Get vehicles belonging to a customer
 */
export async function customerVehicles(customerId) {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.CUSTOMER_VEHICLES,
    [
      Query.equal('customerId', customerId),
      Query.orderDesc('$createdAt')
    ]
  );
  return response.documents;
}

/**
 * Get quote requests for a customer
 */
export async function customerQuoteRequests(customerId) {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.QUOTE_REQUESTS,
    [
      Query.equal('customerId', customerId),
      notDeleted(),
      Query.orderDesc('$createdAt')
    ]
  );
  return response.documents;
}

// =====================================================
// CUSTOMER ORGANISATIONS
// =====================================================

/**
 * Search customer organisations by name
 */
export async function searchCustomerOrganisations(searchTerm, filters = {}) {
  if (!searchTerm || searchTerm.trim().length < 2) return [];
  
  const queries = [
    Query.search('name', searchTerm),
    Query.limit(20)
  ];
  
  if (filters.status) {
    queries.push(Query.equal('status', filters.status));
  }
  if (filters.roles) {
    queries.push(Query.contains('roles', filters.roles));
  }
  
  const response = await appwrite.listDocuments(
    COLLECTIONS.CUSTOMER_ORGANISATIONS,
    queries
  );
  return response.documents;
}

/**
 * Create a customer organisation
 */
export async function createCustomerOrganisation(data) {
  return await appwrite.createDocument(
    COLLECTIONS.CUSTOMER_ORGANISATIONS,
    {
      ...data,
      phones: typeof data.phones === 'string' ? data.phones : JSON.stringify(data.phones || []),
      emails: typeof data.emails === 'string' ? data.emails : JSON.stringify(data.emails || []),
      status: data.status || 'active'
    }
  );
}

/**
 * Get a customer organisation by ID
 */
export async function getCustomerOrganisation(id) {
  return await appwrite.getDocument(COLLECTIONS.CUSTOMER_ORGANISATIONS, id);
}

/**
 * Update a customer organisation
 */
export async function updateCustomerOrganisation(id, data) {
  const updateData = { ...data };
  if (data.phones !== undefined && typeof data.phones !== 'string') {
    updateData.phones = JSON.stringify(data.phones);
  }
  if (data.emails !== undefined && typeof data.emails !== 'string') {
    updateData.emails = JSON.stringify(data.emails);
  }
  return await appwrite.updateDocument(COLLECTIONS.CUSTOMER_ORGANISATIONS, id, updateData);
}

/**
 * List customer organisations with filtering and pagination
 */
export async function listCustomerOrganisations(filters = {}) {
  const queries = [Query.orderDesc('$createdAt')];
  
  if (filters.status) {
    queries.push(Query.equal('status', filters.status));
  }
  if (filters.roles) {
    queries.push(Query.contains('roles', filters.roles));
  }
  if (filters.limit) {
    queries.push(Query.limit(filters.limit));
  }
  if (filters.offset) {
    queries.push(Query.offset(filters.offset));
  }
  
  const response = await appwrite.listDocuments(COLLECTIONS.CUSTOMER_ORGANISATIONS, queries);
  return response;
}

/**
 * Get organisation hierarchy (parent chain + children)
 */
export async function getOrganisationHierarchy(orgId) {
  const org = await appwrite.getDocument(COLLECTIONS.CUSTOMER_ORGANISATIONS, orgId);
  
  // Fetch children
  const childrenResponse = await appwrite.listDocuments(
    COLLECTIONS.CUSTOMER_ORGANISATIONS,
    [Query.equal('parentOrganisationId', orgId)]
  );
  
  // Fetch parent if exists
  let parent = null;
  if (org.parentOrganisationId) {
    try {
      parent = await appwrite.getDocument(COLLECTIONS.CUSTOMER_ORGANISATIONS, org.parentOrganisationId);
    } catch (e) {
      // Parent may have been deleted
    }
  }
  
  return {
    organisation: org,
    parent,
    children: childrenResponse.documents
  };
}

// =====================================================
// CUSTOMER ORGANISATION LINKS (junction table)
// =====================================================

/**
 * Get all organisation links for a customer (enriched with org data)
 */
export async function getCustomerOrganisationLinks(customerId) {
  const response = await appwrite.listDocuments(
    COLLECTIONS.CUSTOMER_ORGANISATION_LINKS,
    [Query.equal('customerId', customerId), notDeleted()]
  );
  
  // Enrich with organisation data
  const enriched = await Promise.all(
    response.documents.map(async (link) => {
      try {
        const org = await appwrite.getDocument(COLLECTIONS.CUSTOMER_ORGANISATIONS, link.customerOrganisationId);
        return { ...link, customerOrganisation: org };
      } catch (e) {
        return { ...link, customerOrganisation: null };
      }
    })
  );
  
  return enriched;
}

/**
 * Get all customer links for an organisation (enriched with customer data)
 */
export async function getOrganisationMembers(customerOrgId) {
  const response = await appwrite.listDocuments(
    COLLECTIONS.CUSTOMER_ORGANISATION_LINKS,
    [Query.equal('customerOrganisationId', customerOrgId), notDeleted()]
  );
  
  // Enrich with customer data
  const enriched = await Promise.all(
    response.documents.map(async (link) => {
      try {
        const customer = await appwrite.getDocument(COLLECTIONS.CUSTOMERS, link.customerId);
        return { ...link, customer };
      } catch (e) {
        return { ...link, customer: null };
      }
    })
  );
  
  return enriched;
}

/**
 * Create a customer-organisation link
 */
export async function createCustomerOrganisationLink(data) {
  // (customerId, customerOrganisationId) is unique, and a removed link is only
  // soft-deleted — so linking the pair again revives it rather than colliding
  // with it.
  const previous = await appwrite.listDocuments(COLLECTIONS.CUSTOMER_ORGANISATION_LINKS, [
    Query.equal('customerId', data.customerId),
    Query.equal('customerOrganisationId', data.customerOrganisationId),
    Query.limit(1)
  ]);
  const removed = previous.documents.find((link) => link.deletedAt);
  if (removed) {
    return await appwrite.updateDocument(COLLECTIONS.CUSTOMER_ORGANISATION_LINKS, removed.$id, {
      role: data.role || '',
      isPrimaryContact: data.isPrimaryContact || false,
      startDate: data.startDate || null,
      endDate: data.endDate || null,
      notes: data.notes || '',
      deletedAt: null,
      updatedAt: new Date().toISOString()
    });
  }

  return await appwrite.createDocument(
    COLLECTIONS.CUSTOMER_ORGANISATION_LINKS,
    {
      customerId: data.customerId,
      customerOrganisationId: data.customerOrganisationId,
      role: data.role || '',
      isPrimaryContact: data.isPrimaryContact || false,
      startDate: data.startDate || null,
      endDate: data.endDate || null,
      notes: data.notes || ''
    }
  );
}

/**
 * Update a customer-organisation link
 */
export async function updateCustomerOrganisationLink(linkId, data) {
  return await appwrite.updateDocument(
    COLLECTIONS.CUSTOMER_ORGANISATION_LINKS,
    linkId,
    {
      ...data
    }
  );
}

/**
 * Remove a customer-organisation link. Soft, so it can be restored; linking the
 * same pair again revives it. Accepts the link or its id.
 */
export async function deleteCustomerOrganisationLink(linkOrId) {
  return await softDelete(COLLECTIONS.CUSTOMER_ORGANISATION_LINKS, linkOrId);
}

// =====================================================
// CUSTOMER RELATIONSHIPS (customer-to-customer)
// =====================================================

/**
 * Get all relationships for a customer (queries both directions)
 */
export async function getCustomerRelationships(customerId) {
  const [responseA, responseB] = await Promise.all([
    appwrite.listDocuments(
      COLLECTIONS.CUSTOMER_RELATIONSHIPS,
      [Query.equal('customerIdA', customerId)]
    ),
    appwrite.listDocuments(
      COLLECTIONS.CUSTOMER_RELATIONSHIPS,
      [Query.equal('customerIdB', customerId)]
    )
  ]);
  
  const allRelationships = [...responseA.documents, ...responseB.documents];
  
  // Enrich with the "other" customer's data
  const enriched = await Promise.all(
    allRelationships.map(async (rel) => {
      const otherId = rel.customerIdA === customerId ? rel.customerIdB : rel.customerIdA;
      try {
        const otherCustomer = await appwrite.getDocument(COLLECTIONS.CUSTOMERS, otherId);
        return { ...rel, relatedCustomer: otherCustomer };
      } catch (e) {
        return { ...rel, relatedCustomer: null };
      }
    })
  );
  
  return enriched;
}

/**
 * Create a customer-to-customer relationship
 */
export async function createCustomerRelationship(data) {
  return await appwrite.createDocument(
    COLLECTIONS.CUSTOMER_RELATIONSHIPS,
    {
      customerIdA: data.customerIdA,
      customerIdB: data.customerIdB,
      relationshipType: data.relationshipType,
      description: data.description || ''
    }
  );
}

/**
 * Delete a customer-to-customer relationship
 */
export async function deleteCustomerRelationship(relationshipId) {
  return await appwrite.deleteDocument(COLLECTIONS.CUSTOMER_RELATIONSHIPS, relationshipId);
}

// =====================================================
// RELATIONSHIP GRAPH DATA
// =====================================================

/**
 * Get relationship graph data for an entity (customer or organisation)
 * Returns { nodes: [...], edges: [...] } formatted for @xyflow/react
 */
export async function getRelationshipGraphData(entityId, entityType) {
  const nodes = [];
  const edges = [];
  
  if (entityType === 'customer') {
    // Get the customer
    const customer = await appwrite.getDocument(COLLECTIONS.CUSTOMERS, entityId);
    nodes.push({
      id: entityId,
      type: 'customer',
      data: { label: `${customer.firstName} ${customer.lastName}`, entity: customer, isCurrent: true },
      position: { x: 0, y: 0 }
    });
    
    // Get organisation links
    const orgLinks = await getCustomerOrganisationLinks(entityId);
    orgLinks.forEach((link, i) => {
      if (link.customerOrganisation) {
        const org = link.customerOrganisation;
        nodes.push({
          id: org.$id,
          type: 'organisation',
          data: { label: org.name, entity: org, roles: org.roles },
          position: { x: 250, y: i * 120 }
        });
        edges.push({
          id: `link-${link.$id}`,
          source: entityId,
          target: org.$id,
          label: link.role || 'member',
          type: 'orgLink'
        });
      }
    });
    
    // Get customer relationships
    const relationships = await getCustomerRelationships(entityId);
    relationships.forEach((rel, i) => {
      if (rel.relatedCustomer) {
        const other = rel.relatedCustomer;
        // Avoid duplicate nodes
        if (!nodes.find(n => n.id === other.$id)) {
          nodes.push({
            id: other.$id,
            type: 'customer',
            data: { label: `${other.firstName} ${other.lastName}`, entity: other },
            position: { x: -250, y: i * 120 }
          });
        }
        edges.push({
          id: `rel-${rel.$id}`,
          source: entityId,
          target: other.$id,
          label: rel.relationshipType,
          type: 'relationship',
          animated: true
        });
      }
    });
    
  } else if (entityType === 'organisation') {
    // Get the organisation
    const org = await appwrite.getDocument(COLLECTIONS.CUSTOMER_ORGANISATIONS, entityId);
    nodes.push({
      id: entityId,
      type: 'organisation',
      data: { label: org.name, entity: org, roles: org.roles, isCurrent: true },
      position: { x: 0, y: 0 }
    });
    
    // Get members
    const members = await getOrganisationMembers(entityId);
    members.forEach((link, i) => {
      if (link.customer) {
        const cust = link.customer;
        nodes.push({
          id: cust.$id,
          type: 'customer',
          data: { label: `${cust.firstName} ${cust.lastName}`, entity: cust },
          position: { x: -250, y: i * 120 }
        });
        edges.push({
          id: `link-${link.$id}`,
          source: cust.$id,
          target: entityId,
          label: link.role || 'member',
          type: 'orgLink'
        });
      }
    });
    
    // Get parent
    if (org.parentOrganisationId) {
      try {
        const parent = await appwrite.getDocument(COLLECTIONS.CUSTOMER_ORGANISATIONS, org.parentOrganisationId);
        nodes.push({
          id: parent.$id,
          type: 'organisation',
          data: { label: parent.name, entity: parent, roles: parent.roles },
          position: { x: 0, y: -150 }
        });
        edges.push({
          id: `parent-${parent.$id}`,
          source: parent.$id,
          target: entityId,
          label: 'parent',
          type: 'hierarchy'
        });
      } catch (e) {
        // Parent may have been deleted
      }
    }
    
    // Get children
    const childrenResponse = await appwrite.listDocuments(
      COLLECTIONS.CUSTOMER_ORGANISATIONS,
      [Query.equal('parentOrganisationId', entityId)]
    );
    childrenResponse.documents.forEach((child, i) => {
      nodes.push({
        id: child.$id,
        type: 'organisation',
        data: { label: child.name, entity: child, roles: child.roles },
        position: { x: 250, y: i * 120 }
      });
      edges.push({
        id: `child-${child.$id}`,
        source: entityId,
        target: child.$id,
        label: 'child',
        type: 'hierarchy'
      });
    });
  }
  
  return { nodes, edges };
}

// =====================================================
// VEHICLES
// =====================================================

/**
 * Get vehicle list
 */
export async function vehicleList(filters = {}) {
  const queries = [Query.orderDesc('$createdAt')];
  
  const response = await appwrite.listDocuments(COLLECTIONS.CUSTOMER_VEHICLES, queries);
  return response.documents;
}

/**
 * Get a single vehicle
 */
export async function vehicleGet(id) {
  return await appwrite.getDocument(COLLECTIONS.CUSTOMER_VEHICLES, id);
}

/**
 * Search vehicles by registration prefix (for reusing an existing vehicle when
 * starting a quote). Falls back to a client-side prefix filter if the attribute
 * has no index for startsWith.
 */
export async function vehicleSearchByRego(regoPrefix) {
  const prefix = (regoPrefix || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (prefix.length < 2) return [];
  try {
    const response = await appwrite.listDocuments(COLLECTIONS.CUSTOMER_VEHICLES, [
      Query.startsWith('registration', prefix),
      Query.limit(8)
    ]);
    return response.documents;
  } catch (err) {
    const response = await appwrite.listDocuments(COLLECTIONS.CUSTOMER_VEHICLES, [Query.limit(200)]);
    return response.documents
      .filter(v => (v.registration || '').toUpperCase().startsWith(prefix))
      .slice(0, 8);
  }
}

/**
 * Create a new vehicle
 */
export async function vehicleCreate(data) {
  return await appwrite.createDocument(
    COLLECTIONS.CUSTOMER_VEHICLES,
    {
      ...data
    }
  );
}

/**
 * Update a vehicle
 */
export async function vehicleUpdate(id, data) {
  return await appwrite.updateDocument(
    COLLECTIONS.CUSTOMER_VEHICLES,
    id,
    {
      ...data
    }
  );
}

/**
 * Get quote requests for a vehicle
 */
export async function vehicleQuoteRequests(vehicleId) {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.QUOTE_REQUESTS,
    [
      Query.equal('customerVehicleId', vehicleId),
      notDeleted(),
      Query.orderDesc('$createdAt')
    ]
  );
  return response.documents;
}

// =====================================================
// QUOTES
// =====================================================

/**
 * Get quotes for a quote request
 */
export async function quoteList(quoteRequestId) {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.QUOTES,
    [
      Query.equal('quoteRequestId', quoteRequestId),
      Query.orderDesc('$createdAt')
    ]
  );
  
  // Parse lines from JSON string stored in Appwrite
  const quotesWithParsedLines = response.documents.map(quote => {
    let lines = [];
    if (quote.lines) {
      try {
        lines = typeof quote.lines === 'string' ? JSON.parse(quote.lines) : quote.lines;
      } catch (e) {
        console.warn('Failed to parse quote lines:', e);
        lines = [];
      }
    }
    return {
      ...quote,
      lines
    };
  });
  
  return quotesWithParsedLines;
}

/**
 * Generate a unique quote number
 * Format: QUO-YYYYMMDD-XXXX (e.g., QUO-20251202-0001)
 */
async function generateQuoteNumber() {
  const today = new Date();
  const datePrefix = today.toISOString().slice(0, 10).replace(/-/g, '');
  const prefix = `QUO-${datePrefix}-`;
  
  // Find the highest quote number for today
  try {
    const user = await getCurrentUser();
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.QUOTES,
      [
        // Only the active organisation's quotes: a user in two organisations can read both.
        Query.equal('organisationId', user.teamId),
        Query.startsWith('quoteNumber', prefix),
        Query.orderDesc('quoteNumber'),
        Query.limit(1)
      ]
    );
    
    if (response.documents.length > 0) {
      const lastNumber = response.documents[0].quoteNumber;
      const lastSeq = parseInt(lastNumber.split('-').pop(), 10) || 0;
      return `${prefix}${String(lastSeq + 1).padStart(4, '0')}`;
    }
  } catch (err) {
    console.warn('Could not query for last quote number:', err.message);
  }
  
  // Default to 0001 if no quotes found or error
  return `${prefix}0001`;
}

/**
 * Create a new quote
 */
export async function quoteCreate(quoteRequestId, data) {
  // Generate quote number if not provided
  const quoteNumber = data.quoteNumber || await generateQuoteNumber();
  
  // Serialize lines array to JSON string for Appwrite storage
  const quoteData = { ...data };
  if (quoteData.lines && Array.isArray(quoteData.lines)) {
    quoteData.lines = JSON.stringify(quoteData.lines);
  }
  
  return await appwrite.createDocument(
    COLLECTIONS.QUOTES,
    {
      ...quoteData,
      quoteNumber,
      quoteRequestId
    }
  );
}

/**
 * Get the single canonical quote for a quote request, creating a blank one if
 * none exists. Enforces one quote per request: if several exist (legacy data
 * from the old multi-quote UI), the newest is kept and the rest are deleted.
 * Returns the quote with `lines` parsed to an array.
 */
export async function getOrCreateQuote(quoteRequestId) {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.QUOTES,
    [
      Query.equal('quoteRequestId', quoteRequestId),
      Query.orderDesc('$createdAt')
    ]
  );

  let quote;
  if (response.documents.length > 0) {
    quote = response.documents[0];
    // Collapse legacy multi-quote data to a single quote: keep newest, drop rest.
    const extras = response.documents.slice(1);
    if (extras.length > 0) {
      await Promise.all(
        extras.map(q => appwrite.deleteDocument(COLLECTIONS.QUOTES, q.$id).catch((err) => {
          console.warn('Failed to delete extra quote during single-quote migration:', err.message);
        }))
      );
    }
  } else {
    const quoteNumber = await generateQuoteNumber();
    quote = await appwrite.createDocument(
      COLLECTIONS.QUOTES,
      {
        quoteRequestId,
        quoteNumber,
        status: 'draft',
        subtotal: 0,
        gst: 0,
        total: 0,
        lines: '[]'
      }
    );
  }

  let lines = [];
  if (quote.lines) {
    try {
      lines = typeof quote.lines === 'string' ? JSON.parse(quote.lines) : quote.lines;
    } catch (e) {
      console.warn('Failed to parse quote lines:', e);
      lines = [];
    }
  }
  return { ...quote, lines };
}

/**
 * Update a quote
 */
export async function quoteUpdate(id, data) {
  // Serialize lines array to JSON string for Appwrite storage
  const quoteData = { ...data };
  if (quoteData.lines && Array.isArray(quoteData.lines)) {
    quoteData.lines = JSON.stringify(quoteData.lines);
  }

  // No `updatedAt` here: the quotes table has never had that column, and
  // Appwrite rejects the *entire* write when it is sent an attribute the table
  // does not define. That silently failed every quote save — lines, totals and
  // notes all stayed at their created values — and went unnoticed because the
  // editor recomputes its lines from vehicle_damage rather than reading them
  // back. Appwrite's own `$updatedAt` already records the write time.
  return await appwrite.updateDocument(COLLECTIONS.QUOTES, id, quoteData);
}

/**
 * Update a quote line
 */
export async function quoteLineUpdate(lineId, data) {
  return await appwrite.updateDocument(
    COLLECTIONS.QUOTE_LINES,
    lineId,
    {
      ...data
    }
  );
}

// =====================================================
// TEAM MEMBERS & USERS (via user-management function)
// =====================================================

/**
 * Get team members with their labels (roles)
 * Uses user-management function to get user labels
 */
export async function userList() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  try {
    const result = await appwrite.executeFunction(
      FUNCTION_IDS.USER_MANAGEMENT,
      JSON.stringify({ 
        action: 'list-users',
        teamId: user.teamId
      }),
      '/',
      'POST'
    );

    if (result.success && result.users) {
      return result.users;
    }
    
    // Fallback to basic team members if function fails
    console.warn('user-management function failed, falling back to basic team members');
    return await appwrite.getTeamMembers();
  } catch (error) {
    console.error('Error listing users:', error);
    // Fallback to basic team members
    return await appwrite.getTeamMembers();
  }
}

/**
 * Get current user
 */
export async function userGetCurrent() {
  return await getCurrentUser();
}

/**
 * Invite a new user to the team.
 *
 * The function adds the membership, creates their user_profiles row and sends
 * the one invitation email. Returns `emailSent: false` (with a reason) when the
 * person was added but the email did not go — e.g. the organisation's daily cap.
 */
export async function userCreate(data) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const result = await appwrite.executeFunction(
    FUNCTION_IDS.USER_MANAGEMENT,
    JSON.stringify({
      action: 'invite-user',
      teamId: user.teamId,
      email: data.email,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      phone: data.phone || '',
      labels: data.labels || data.roles || ['viewer'],
      teamRoles: ['member']
    }),
    '/',
    'POST'
  );

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to invite user');
  }

  return {
    ...result.membership,
    profile: result.profile,
    emailSent: result.emailSent,
    emailSkippedReason: result.emailSkippedReason
  };
}

// =====================================================
// USER PROFILES (#68) — read and edited directly, no function round trip
// =====================================================

/**
 * Every user_profiles row for the organisation, in list order.
 */
export async function userProfileList(teamId) {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.USER_PROFILES,
    [
      Query.equal('organisationId', teamId),
      Query.orderAsc('sortOrder'),
      Query.limit(500)
    ]
  );
  return response.documents;
}

/**
 * Update a profile row. Owners can edit any row in their organisation and a
 * person can edit their own; row permissions enforce both. Name, phone and
 * status reach the Auth user via the user-profile-sync trigger.
 */
export async function userProfileUpdate(profileId, data) {
  return await databases.updateDocument(DATABASE_ID, COLLECTIONS.USER_PROFILES, profileId, data);
}

/**
 * Create rows for members who joined before user_profiles existed.
 */
export async function userProfilesBackfill() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const result = await appwrite.executeFunction(
    FUNCTION_IDS.USER_MANAGEMENT,
    JSON.stringify({ action: 'backfill-profiles', teamId: user.teamId }),
    '/',
    'POST'
  );
  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to set up user profiles');
  }
  return result.created;
}

/**
 * Send the invitation email again. Heavily limited server-side: once a day,
 * four sends per person in total, and a daily cap per organisation.
 */
export async function userResendInvite(userId) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const result = await appwrite.executeFunction(
    FUNCTION_IDS.USER_MANAGEMENT,
    JSON.stringify({ action: 'resend-invite', teamId: user.teamId, userId }),
    '/',
    'POST'
  );
  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to resend the invitation');
  }
  return result.profile;
}

/**
 * Mark the signed-in person's invitation to this organisation as accepted.
 * Called once per sign-in; a no-op unless their row is still pending.
 * Never throws — it is bookkeeping, not something to put in front of the user.
 */
export async function userProfileMarkAccepted(currentUser) {
  if (!currentUser?.teamId || !currentUser?.id) return;
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.USER_PROFILES,
      [
        Query.equal('organisationId', currentUser.teamId),
        Query.equal('userId', currentUser.id),
        Query.limit(1)
      ]
    );
    const own = response.documents[0];
    if (own && !own.acceptedAt) {
      await userProfileUpdate(own.$id, { acceptedAt: new Date().toISOString() });
    }
  } catch (error) {
    console.warn('Could not record invitation acceptance:', error.message);
  }
}

/**
 * Update team membership roles
 * Uses direct Teams API call - only 'owner' role can update membership roles
 */
export async function membershipUpdateRoles(membershipId, roles) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  // Use Teams API directly to update membership roles
  try {
    const updatedMembership = await appwrite.teams.updateMembership({
      teamId: user.teamId,
      membershipId: membershipId,
      roles: roles
    });
    return updatedMembership;
  } catch (error) {
    throw new Error(error.message || 'Failed to update membership roles');
  }
}

/**
 * Update user labels (roles) - DEPRECATED
 * Use membershipUpdateRoles instead for team-based role management
 */
export async function userUpdateLabels(userId, labels) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const result = await appwrite.executeFunction(
    FUNCTION_IDS.USER_MANAGEMENT,
    JSON.stringify({
      action: 'update-labels',
      teamId: user.teamId,
      userId,
      labels
    }),
    '/',
    'POST'
  );

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update labels');
  }

  return result.user;
}

// Alias for backward compatibility
export const userUpdateRoles = membershipUpdateRoles;

/**
 * Update user name
 */
export async function userUpdateName(userId, name) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const result = await appwrite.executeFunction(
    FUNCTION_IDS.USER_MANAGEMENT,
    JSON.stringify({
      action: 'update-name',
      teamId: user.teamId,
      userId,
      name
    }),
    '/',
    'POST'
  );

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update name');
  }

  return result.user;
}

/**
 * Update user phone
 */
export async function userUpdatePhone(userId, phone) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const result = await appwrite.executeFunction(
    FUNCTION_IDS.USER_MANAGEMENT,
    JSON.stringify({
      action: 'update-phone',
      teamId: user.teamId,
      userId,
      phone
    }),
    '/',
    'POST'
  );

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update phone');
  }

  return result.user;
}

/**
 * Update user status (active/blocked)
 */
export async function userUpdateStatus(userId, status) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const result = await appwrite.executeFunction(
    FUNCTION_IDS.USER_MANAGEMENT,
    JSON.stringify({
      action: 'update-status',
      teamId: user.teamId,
      userId,
      status
    }),
    '/',
    'POST'
  );

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update status');
  }

  return result.user;
}

/**
 * Remove user from team
 */
export async function userRemove(membershipId) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const result = await appwrite.executeFunction(
    FUNCTION_IDS.USER_MANAGEMENT,
    JSON.stringify({
      action: 'remove-user',
      teamId: user.teamId,
      membershipId
    }),
    '/',
    'POST'
  );

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to remove user');
  }

  return true;
}

// =====================================================
// ORGANISATION / TEAM
// =====================================================

/**
 * Get organisation details (now returns team info)
 */
export async function organisationGet() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');
  
  const team = await appwrite.getCurrentTeam();
  
  // Return in expected format
  return {
    $id: team.$id,
    name: team.name,
    // Team prefs can store additional org info
    ...team.prefs
  };
}

/**
 * Update organisation
 */
export async function organisationUpdate(id, data) {
  if (data.name) {
    await appwrite.updateTeam(data.name);
  }
  // Additional settings stored in team prefs via organisationSettingsUpdate
}

/**
 * Get customers for current organisation
 */
export async function organisationGetCustomers() {
  return await customerList();
}

// =====================================================
// ORGANISATION SETTINGS (stored in Team Prefs)
// =====================================================

/**
 * Get organisation settings from team preferences
 */
export async function organisationSettingsGet() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');
  
  const team = await appwrite.getCurrentTeam();
  
  return {
    $id: user.teamId,
    organisationId: user.teamId,
    // General settings from team prefs
    quoteNumberPrefix: team.prefs?.quoteNumberPrefix || 'Q',
    quoteNumberNext: team.prefs?.quoteNumberNext || 1,
    jobNumberPrefix: team.prefs?.jobNumberPrefix || 'J',
    jobNumberNext: team.prefs?.jobNumberNext || 1,
    defaultGstRate: team.prefs?.defaultGstRate || 15.00,
    defaultQuoteValidDays: team.prefs?.defaultQuoteValidDays || 30,
    // Panel Quote state (credentials stored server-side)
    panelQuoteEnabled: team.prefs?.panelQuoteEnabled || false,
    panelQuoteUsername: team.prefs?.panelQuoteUsername || ''
    // Note: password is never returned client-side
  };
}

/**
 * Update organisation settings in team preferences
 */
export async function organisationSettingsUpdate(id, data) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');
  
  const team = await appwrite.getCurrentTeam();
  const updatedPrefs = {
    ...team.prefs,
    ...data
  };
  
  // Team prefs update is done via teams SDK
  // Note: This requires team owner/admin access
  await appwrite.teams.updatePrefs(user.teamId, updatedPrefs);
  
  return { ...data, $id: user.teamId };
}

/**
 * Create organisation settings (creates team prefs if needed)
 * For teams, settings already exist in prefs, so this just updates
 */
export async function organisationSettingsCreate(data) {
  return await organisationSettingsUpdate(null, data);
}

// =====================================================
// CUSTOMER INTAKE FORM (branding + availability)
//
// What a customer sees at /:teamId/form. The logo lives in the public-read
// `org-branding` bucket because anonymous customers have to load it; the rest
// is team prefs. See functions/customer-get-team, which serves these to the
// customer app, and docs/technical/customer-intake-workflow.md.
// =====================================================

/**
 * The four colours a shop can set, and the prefs key each is stored under.
 * Kept in step with functions/customer-get-team (buildBranding) and the
 * customer app's lib/branding.js.
 */
export const BRAND_COLOUR_KEYS = {
  accent: 'brandAccent',
  accentText: 'brandAccentText',
  background: 'brandBackground',
  text: 'brandText'
};

/**
 * Settings behind the public quote request form.
 * `formUrl` is the link a shop hands to customers — it is derived, not stored.
 */
export async function intakeSettingsGet() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const team = await appwrite.getCurrentTeam();
  const prefs = team.prefs || {};

  const colours = {};
  for (const [name, key] of Object.entries(BRAND_COLOUR_KEYS)) {
    colours[name] = prefs[key] || '';
  }

  let services = [];
  try {
    const parsed = JSON.parse(prefs.upsellServices || '[]');
    if (Array.isArray(parsed)) services = parsed;
  } catch {
    services = [];
  }

  return {
    acceptingQuotes: prefs.acceptingQuotes !== false,
    colours,
    services,
    // The shop's own name, so the settings preview shows the real header.
    shopName: team.name || '',
    brandLogoFileId: prefs.brandLogoFileId || '',
    logoUrl: prefs.brandLogoFileId
      ? appwrite.getFileViewUrl(BUCKETS.ORG_BRANDING, prefs.brandLogoFileId)
      : '',
    // Set per environment by the deploy workflows. Drop a trailing slash so an
    // override like "https://rfq.panelgear.com/" still gives a working link.
    formUrl: `${(process.env.REACT_APP_CUSTOMER_FORM_URL || '').replace(/\/+$/, '')}/${user.teamId}/form`
  };
}

/**
 * Save intake settings. Pass `logoFile` to replace the logo, or
 * `removeLogo: true` to clear it. `colours` is the four-key object above.
 */
export async function intakeSettingsSave({ acceptingQuotes, colours, services, logoFile, removeLogo } = {}) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const team = await appwrite.getCurrentTeam();
  const prefs = team.prefs || {};
  const previousLogoId = prefs.brandLogoFileId || '';

  let brandLogoFileId = previousLogoId;

  if (logoFile) {
    const fileId = ID.unique();
    // No per-file permissions: the bucket is fileSecurity:false with
    // read("any"), which is what makes the logo loadable by a customer who
    // has no session at all.
    await storage.createFile(BUCKETS.ORG_BRANDING, fileId, logoFile);
    brandLogoFileId = fileId;
  } else if (removeLogo) {
    brandLogoFileId = '';
  }

  const colourPrefs = {};
  for (const [name, key] of Object.entries(BRAND_COLOUR_KEYS)) {
    colourPrefs[key] = colours?.[name] || '';
  }

  // Team prefs are a single JSON blob with a size ceiling, so the extras list
  // is stored trimmed and capped rather than as whatever the form held.
  const upsellServices = JSON.stringify(
    (Array.isArray(services) ? services : [])
      .filter((service) => service?.label?.trim())
      .slice(0, 20)
      .map((service, index) => ({
        id: service.id || `service-${Date.now()}-${index}`,
        label: service.label.trim().slice(0, 120),
        price: String(service.price || '').trim().slice(0, 40)
      }))
  );

  await appwrite.teams.updatePrefs(user.teamId, {
    ...prefs,
    acceptingQuotes: acceptingQuotes !== false,
    ...colourPrefs,
    upsellServices,
    brandLogoFileId
  });

  // Only bin the old file once the new one is recorded — a failed prefs write
  // would otherwise leave the shop with no logo at all.
  if (previousLogoId && previousLogoId !== brandLogoFileId) {
    await storage.deleteFile(BUCKETS.ORG_BRANDING, previousLogoId).catch(() => null);
  }

  return { acceptingQuotes, colours, brandLogoFileId };
}

/** Blank row for the extras editor. */
export function newIntakeService() {
  return { id: `service-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, label: '', price: '' };
}

// =====================================================
// PANEL QUOTE INTEGRATION
// Non-sensitive settings stored in team preferences.
// Password encrypted via credential-store function (AES-256-GCM).
// =====================================================

/**
 * Get Panel Quote settings
 * Reads non-sensitive settings from team prefs.
 * Password is stored encrypted — only server functions can decrypt it.
 */
export async function panelQuoteSettingsGet() {
  try {
    const user = await getCurrentUser();
    if (!user?.teamId) throw new Error('No team selected');

    const team = await appwrite.getCurrentTeam();
    const prefs = team.prefs || {};

    return {
      enabled: prefs.panelQuoteEnabled || false,
      username: prefs.panelQuoteUsername || '',
      hasPassword: prefs.panelQuoteHasPassword || false,
      baseUrl: prefs.panelQuoteBaseUrl || '',
      damageCategory: prefs.panelQuoteDamageCategory || 'Accident Damage',
      configured: !!(prefs.panelQuoteEnabled && prefs.panelQuoteUsername && prefs.panelQuoteHasPassword)
    };
  } catch (error) {
    console.error('Error getting Panel Quote settings:', error);
  }

  return {
    enabled: false,
    username: '',
    hasPassword: false,
    damageCategory: 'Accident Damage',
    configured: false
  };
}

/**
 * Save Panel Quote settings
 * Stores non-sensitive settings in team prefs.
 * Sends password to credential-store function for AES-256-GCM encryption.
 */
export async function panelQuoteSettingsSave(data) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const team = await appwrite.getCurrentTeam();
  const hasNewPassword = !!(data.password && data.password.trim());

  // Update team preferences with non-sensitive settings
  const updatedPrefs = {
    ...team.prefs,
    panelQuoteEnabled: data.enabled,
    panelQuoteUsername: data.username || '',
    panelQuoteBaseUrl: data.baseUrl || '',
    panelQuoteDamageCategory: data.damageCategory || 'Accident Damage',
    panelQuoteHasPassword: hasNewPassword || team.prefs?.panelQuoteHasPassword || false
  };
  await teams.updatePrefs(user.teamId, updatedPrefs);

  // Encrypt and store password via the credential-store function
  // The function encrypts with AES-256-GCM using a key only available to server functions
  if (hasNewPassword) {
    const result = await appwrite.executeFunction(
      FUNCTION_IDS.CREDENTIAL_STORE,
      JSON.stringify({
        teamId: user.teamId,
        password: data.password.trim(),
        username: data.username || ''
      }),
      '/store',
      'POST'
    );

    if (!result?.success) {
      throw new Error(result?.error || 'Failed to store credentials');
    }
  }

  return {
    enabled: data.enabled,
    username: data.username,
    hasPassword: hasNewPassword || team.prefs?.panelQuoteHasPassword || false
  };
}

/**
 * Test Panel Quote connection
 */
export async function panelQuoteTestConnection() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const result = await appwrite.executeFunction(
    FUNCTION_IDS.PANEL_QUOTE_TEST,
    JSON.stringify({ teamId: user.teamId }),
    '/test',
    'POST'
  );
  return result;
}

/**
 * List cached Panel Quote quotes for the current organisation.
 * Rows are written by the panel-quote-list-quotes function; only rows still
 * returned by Panel Quote (status === 'active') are shown for selection.
 */
export async function panelQuoteCachedQuotes() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.PANEL_QUOTE_QUOTES,
    [
      Query.equal('organisationId', user.teamId),
      Query.equal('status', 'active'),
      Query.orderAsc('sortOrder'),
      Query.limit(500)
    ]
  );
  return response.documents;
}

/**
 * Get the Panel Quote sync status row for the current organisation.
 * Returns null when no sync has ever run.
 */
export async function panelQuoteSyncStatusGet() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  try {
    return await databases.getDocument(DATABASE_ID, COLLECTIONS.PANEL_QUOTE_STATUS, user.teamId);
  } catch (err) {
    if (err?.code === 404) return null;
    throw err;
  }
}

/**
 * Trigger a Panel Quote sync (async — fire and forget).
 * The panel-quote-list-quotes function scrapes Panel Quote and writes results
 * into panel-quote-quotes; the UI receives them via realtime subscriptions.
 * Progress is tracked on the panel-quote-status row.
 */
export async function panelQuoteSyncQuotes() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  return await appwrite.executeFunctionAsync(
    FUNCTION_IDS.PANEL_QUOTE_LIST_QUOTES,
    JSON.stringify({ teamId: user.teamId }),
    '/list',
    'POST'
  );
}

/**
 * Import job from Panel Quote (async — fire and forget)
 * The function runs in the background; progress is tracked via function_runs.
 */
export async function panelQuoteImport(jobId) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');
  
  const result = await appwrite.executeFunctionAsync(
    FUNCTION_IDS.PANEL_QUOTE_IMPORT,
    JSON.stringify({ jobNumber: jobId, teamId: user.teamId }),
    '/import',
    'POST'
  );
  return result;
}

/**
 * Import several Panel Quote jobs in ONE function execution (async).
 * Sending one execution per job made them all log in to Panel Quote at once and
 * the later ones were refused (HQ #116). The function walks the list in order,
 * and hands over to a fresh execution itself on long lists.
 */
export async function panelQuoteImportBatch(jobIds) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const batchId = Math.random().toString(36).slice(2, 10);
  return await appwrite.executeFunctionAsync(
    FUNCTION_IDS.PANEL_QUOTE_IMPORT,
    JSON.stringify({ jobNumbers: jobIds, teamId: user.teamId, batchId }),
    '/import',
    'POST'
  );
}

/**
 * Hand a quote request off to Panel Quote as a new job (async — fire and forget).
 *
 * The function runs Panel Quote's new-job wizard and saves the quote's lines in
 * one pass, then writes panelQuoteJobId/Number back onto the request — which is
 * how the UI learns it worked, via the realtime subscription.
 */
export async function panelQuoteCreateJob(quoteRequestId, quoteId = null) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  return await appwrite.executeFunctionAsync(
    FUNCTION_IDS.PANEL_QUOTE_UPDATE_QUOTE,
    JSON.stringify({ action: 'create', quoteRequestId, quoteId, teamId: user.teamId }),
    '/',
    'POST'
  );
}

// =====================================================
// PANEL QUOTE ADDRESS BOOK & INSURER MAPPING
// =====================================================

/**
 * Trigger a Panel Quote address-book sync (async — fire and forget).
 * Progress lands on the panel-quote-status row, which the Settings screen
 * watches; the book runs to thousands of rows, well past a sync execution.
 */
export async function panelQuoteSyncCustomers() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  return await appwrite.executeFunctionAsync(
    FUNCTION_IDS.PANEL_QUOTE_LIST_CUSTOMERS,
    JSON.stringify({ teamId: user.teamId, action: 'sync' }),
    '/',
    'POST'
  );
}

/**
 * Read the cached Panel Quote address book.
 * @param {string|null} customerType 'insurance' | 'personal' | 'business', or null for all
 */
export async function panelQuoteCachedCustomers(customerType = null) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const queries = [
    Query.equal('organisationId', user.teamId),
    Query.equal('status', 'active'),
    Query.limit(500)
  ];
  if (customerType) queries.push(Query.equal('customerType', customerType));

  // Favourites first, then alphabetical — the shop's own favourites are what
  // they reach for, so they belong at the top of every picker.
  const documents = [];
  let offset = 0;
  for (;;) {
    const page = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.PANEL_QUOTE_CUSTOMERS,
      [...queries, Query.offset(offset)]
    );
    documents.push(...page.documents);
    if (page.documents.length < 500) break;
    offset += 500;
  }

  return documents.sort((a, b) => {
    if (!!a.isFavourite !== !!b.isFavourite) return a.isFavourite ? -1 : 1;
    return (a.name || '').localeCompare(b.name || '');
  });
}

/** Every PanelGear insurer → Panel Quote customer mapping for this organisation. */
export async function panelQuoteInsurerMappings() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.PANEL_QUOTE_INSURER_MAP,
    [Query.equal('organisationId', user.teamId), notDeleted(), Query.limit(500)]
  );
  return response.documents;
}

/**
 * Find this organisation's mapping row for an insurer.
 *
 * Looked up by query rather than a composite `{teamId}-{insurerId}` document id:
 * insurer ids are 36-character UUIDs, so that id would be 57 characters and
 * Appwrite rejects anything over 36. The organisationId+insurerId index exists
 * for exactly this.
 */
async function findInsurerMappingDocument(teamId, insurerId) {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.PANEL_QUOTE_INSURER_MAP,
    [Query.equal('organisationId', teamId), Query.equal('insurerId', insurerId), notDeleted(), Query.limit(1)]
  );
  return response.documents[0] || null;
}

/**
 * Ask the server to propose mappings for the insurers that don't have one.
 * Proposals are returned for review — nothing is saved until the user confirms.
 */
export async function panelQuoteAutoMapInsurers() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  return await appwrite.executeFunction(
    FUNCTION_IDS.PANEL_QUOTE_LIST_CUSTOMERS,
    JSON.stringify({ teamId: user.teamId, action: 'automap' }),
    '/',
    'POST'
  );
}

/** Save a confirmed insurer mapping, replacing any mapping already held for it. */
export async function panelQuoteSaveInsurerMapping({
  insurerId,
  insurerName,
  panelQuoteCustomerId,
  panelQuoteCustomerName,
  source = 'manual',
  confidence = ''
}) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');
  if (!insurerId || !panelQuoteCustomerId) throw new Error('Both an insurer and a Panel Quote customer are required');

  const data = {
    insurerId,
    insurerName: insurerName || '',
    panelQuoteCustomerId: String(panelQuoteCustomerId),
    panelQuoteCustomerName: panelQuoteCustomerName || '',
    source,
    confidence: confidence || '',
    updatedBy: user.$id
  };

  const existing = await findInsurerMappingDocument(user.teamId, insurerId);
  if (existing) {
    return await appwrite.updateDocument(COLLECTIONS.PANEL_QUOTE_INSURER_MAP, existing.$id, data);
  }

  return await appwrite.createDocument(
    COLLECTIONS.PANEL_QUOTE_INSURER_MAP,
    { ...data, createdBy: user.$id }
  );
}

/** Remove a mapping, so the insurer shows as unmapped again. */
export async function panelQuoteDeleteInsurerMapping(insurerId) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const existing = await findInsurerMappingDocument(user.teamId, insurerId);
  if (!existing) return null;
  return await softDelete(COLLECTIONS.PANEL_QUOTE_INSURER_MAP, existing);
}

// =====================================================
// DAMAGE ASSESSMENT
// =====================================================

/**
 * Explicitly run the AI damage assessment for a quote request.
 * ML detection runs automatically as images upload (orchestrator 'ml-only' mode);
 * this triggers the full pipeline: AI part assessment -> aggregation -> quote.
 *
 * Neither option touches the photos' ML results — re-running the assessment must
 * not undo the angle model's left/right calls. Wiping ML and detecting from
 * scratch is a separate, dev-only action (the reassess-damage function).
 *
 * @param {Object} options
 * @param {boolean} options.restart - discard the previous AI worker runs and
 *   assess again (otherwise a completed run just reports "already done").
 * @param {boolean} options.merge - fold the findings into the assessment already
 *   on the record instead of replacing it ("Update with AI").
 */
export async function generateDamageAssessment(quoteRequestId, options = {}) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const { restart = false, merge = false } = options;

  return await appwrite.executeFunctionAsync(
    'ai-damage-assessment-orchestrator',
    JSON.stringify({
      quoteRequestId,
      organisationId: user.teamId,
      mode: 'full',
      restart,
      merge
    }),
    '/',
    'POST'
  );
}

// =====================================================
// QUOTE GENERATION
// =====================================================

/**
 * Create a placeholder quote with isGenerating = true so the UI shows
 * immediate feedback while the function cold-starts and runs.
 */
export async function createGeneratingPlaceholder(quoteRequestId) {
  const quoteNumber = await generateQuoteNumber();
  const placeholder = await appwrite.createDocument(
    COLLECTIONS.QUOTES,
    {
      quoteRequestId,
      quoteNumber,
      status: 'draft',
      subtotal: 0,
      gst: 0,
      total: 0,
      isGenerating: true,
      lines: '[]'
    }
  );
  return placeholder;
}

/**
 * Generate quote from damage assessment.
 * Creates a placeholder first for instant UI feedback, then fires the
 * function which fills in and finalises the placeholder.
 */
export async function generateQuoteFromDamage(quoteRequestId, options = {}) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');
  const { insurerId = null, labourRate = null, placeholderQuoteId = null } = options;
  
  const result = await appwrite.executeFunctionAsync(
    FUNCTION_IDS.GENERATE_QUOTE_FROM_DAMAGE,
    JSON.stringify({ 
      quoteRequestId,
      organisationId: user.teamId,
      insurerId,
      labourRate,
      placeholderQuoteId
    }),
    '/generate',
    'POST'
  );
  return result;
}

// =====================================================
// LOOKUP DATA (Public)
// =====================================================

/**
 * Get regions list
 */
export async function regionList() {
  return await appwrite.getRegions();
}

/**
 * Get insurers list
 */
export async function insurerList() {
  return await appwrite.getInsurers();
}

export async function insurerConfigGet(configId) {
  if (!configId) throw new Error('Insurer config id is required');
  return await appwrite.getDocument(COLLECTIONS.INSURER_CONFIGS, configId);
}

export async function insurerGuidelines(configId) {
  if (!configId) throw new Error('Insurer config id is required');

  let config = await insurerConfigGet(configId);
  let content = '';
  let downloadUrl = null;

  // If child config has no guidelines, check parent
  if (!config?.repairGuidelinesFileId && config?.parentId) {
    const parentConfig = await insurerConfigGet(config.parentId);
    if (parentConfig?.repairGuidelinesFileId) {
      config = parentConfig;
    }
  }

  if (config?.repairGuidelinesFileId) {
    downloadUrl = appwrite.getFileDownload(BUCKETS.REPAIR_GUIDELINES, config.repairGuidelinesFileId);
    try {
      content = await appwrite.getFileText(BUCKETS.REPAIR_GUIDELINES, config.repairGuidelinesFileId);
    } catch (err) {
      console.error('Failed to load insurer guidelines file', err);
      content = '';
    }
  }

  return { config, content, downloadUrl };
}

/**
 * Get vehicle makes and models
 */
export async function makesModelsList() {
  return await appwrite.getMakesModels();
}

// The guide for one insurer, cached for the session, for the reason on
// shopLineCache — and this is the bigger of the two reads: a guide runs to
// several hundred lines (191 for IAG, 394 for Ando), so one lookup is two to
// four paginated requests, and the damage screen was mounting 54 lookups.
// A guide is seeded, and nothing in this app edits one, so read it once.
const insurerPartsCache = new Map(); // `teamId:insurerConfigId` -> Promise<rows[]>

/** Forget every cached guide. */
export function clearInsurerPartsCache() {
  insurerPartsCache.clear();
}

/** Every line allowance for a config, following the parent when it has none. */
async function loadInsurerLineAllowances(insurerConfigId) {
  const page = async (configId, after) => {
    const queries = [Query.equal('insurerConfigId', configId), Query.limit(100)];
    if (after) queries.push(Query.cursorAfter(after));
    const response = await appwrite.listDocuments(COLLECTIONS.INSURER_LINE_ALLOWANCES, queries);
    return response.documents || [];
  };

  // Uses cursor-based pagination to fetch ALL line allowances (default Appwrite limit is 25)
  const readAll = async (configId) => {
    let rows = [];
    let lastId = null;
    for (;;) {
      const docs = await page(configId, lastId);
      rows = rows.concat(docs);
      if (docs.length < 100) return rows;
      lastId = docs[docs.length - 1].$id;
    }
  };

  let parts = await readAll(insurerConfigId);

  // If no parts found and config has a parent, retry with parent config
  if (parts.length === 0) {
    const config = await insurerConfigGet(insurerConfigId);
    if (config?.parentId) parts = await readAll(config.parentId);
  }
  return parts;
}

/**
 * Get insurer line allowances (parts) for a specific insurer config
 * @param {string} insurerConfigId - The insurer config ID
 * @param {string} [zone] - Optional zone to filter by (e.g. 'front', 'rear')
 * @param {string} [searchTerm] - Optional search term to filter by
 * @returns {Promise<Array>} List of parts/line allowances
 */
export async function getInsurerParts(insurerConfigId, zone = null, searchTerm = null) {
  // No insurer is private work, not a missing argument. There is no guide to
  // read, and the shop's own lines are the only lines there are — refusing here
  // is what left the part lookup empty on every private job.
  let parts = [];
  if (insurerConfigId) {
    const user = await getCurrentUser().catch(() => null);
    const key = `${user?.teamId || 'no-team'}:${insurerConfigId}`;
    if (!insurerPartsCache.has(key)) {
      insurerPartsCache.set(
        key,
        loadInsurerLineAllowances(insurerConfigId).catch((err) => {
          insurerPartsCache.delete(key); // never remember a failed read
          throw err;
        }),
      );
    }
    parts = await insurerPartsCache.get(key);
  }

  // Filter by zone if specified
  if (zone && parts.length > 0) {
    const zoneColumnMap = {
      'front': 'locationFront',
      'front-right': ['locationFront', 'locationFrontRight', 'locationRightSide'],
      'right': 'locationRightSide',
      'rear-right': ['locationRear', 'locationRearRight', 'locationRightSide'],
      'rear': 'locationRear',
      'rear-left': ['locationRear', 'locationRearLeft', 'locationLeftSide'],
      'left': 'locationLeftSide',
      'front-left': ['locationFront', 'locationFrontLeft', 'locationLeftSide'],
      'roof': 'locationRoof',
      'underside': 'locationUnderside',
      'other': null // Show all for 'other'
    };

    const locationFields = zoneColumnMap[zone];
    
    if (locationFields) {
      parts = parts.filter(part => {
        if (Array.isArray(locationFields)) {
          // Check if any of the location fields is true
          return locationFields.some(field => part[field] === 1 || part[field] === true || part[field] === '1');
        } else {
          // Check single location field
          return part[locationFields] === 1 || part[locationFields] === true || part[locationFields] === '1';
        }
      });
    }
    // If zone is 'other' or no mapping, show all parts
  }

  // Filter by search term if specified
  if (searchTerm && searchTerm.trim()) {
    const term = searchTerm.toLowerCase();
    parts = parts.filter(part => {
      const lineName = (part.lineName || '').toLowerCase();
      const searchTerms = (part.searchTerms || '').toLowerCase();
      
      return lineName.includes(term) || 
             searchTerms.includes(term);
    });
  }

  // ── The shop's own lines ────────────────────────────────────────────────
  //
  // The guides do not carry every line a shop bills, so an assessor looking for
  // a cant rail or an arch moulding clip set has to be able to find one here —
  // otherwise the sheet exists but nobody can put a line from it on a quote.
  //
  // Scoped the same way the pipeline scopes it, and for the same reason: a line
  // narrowed to Ando must not be offered on an IAG job, or the assessor picks a
  // line the quote generator will then decline to price.
  //
  // Never fatal. These lines are an addition to the guide, so a sheet that
  // cannot be read costs them and nothing else — the assessor still gets the
  // insurer's catalogue, which is what this screen had before the sheet existed.
  let shopLines = [];
  try {
    shopLines = await shopLinesForLookup(insurerConfigId, zone, searchTerm);
  } catch (err) {
    console.warn('Shop line sheet unavailable for the part lookup', err);
  }

  // Sort by line name. Concat first even when there are no shop lines: `parts`
  // may still be the cached guide itself (no zone, no search term), and sorting
  // in place would reorder what every other lookup is holding.
  return parts.concat(shopLines)
    .sort((a, b) => (a.lineName || '').localeCompare(b.lineName || ''));
}

/**
 * The shop's own lines, in the shape the part lookup renders.
 *
 * Standards plus this organisation's own, with the organisation's overrides
 * folded on top and anything it has switched off left out — the same three
 * layers the pipeline resolves, so what an assessor can pick is exactly what a
 * quote can price.
 *
 * `insurerConfigId` of null means private work: no insurer, so the shop's own
 * rates are the only rates there are.
 */
async function shopLinesForLookup(insurerConfigId, zone = null, searchTerm = null) {
  const user = await getCurrentUser();
  if (!user?.teamId) return [];

  const rows = await shopLineList();

  const isPrivate = !insurerConfigId;
  const visible = rows.filter((row) => {
    if (row.disabled) return false;
    // An edited standard is quoted at the shop's numbers, not the platform's.
    const effective = row.override ? { ...row, ...stripBlank(row.override) } : row;
    if (isPrivate) return effective.appliesToPrivate !== false;
    const scope = shopLineList_parse(effective.insurerConfigIds);
    return scope.length === 0 || scope.includes(insurerConfigId);
  });

  const zoneMatches = (row) => {
    if (!zone || zone === 'other') return true;
    const zones = shopLineList_parse(row.locations);
    // A line tied to no section is quotable anywhere — frame tape and a hub cap
    // are not about a panel at all.
    return zones.length === 0 || zones.includes(zone);
  };

  const termMatches = (row) => {
    if (!searchTerm || !searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return `${row.lineName || ''} ${row.searchTerms || ''}`.toLowerCase().includes(term);
  };

  return visible
    .map((row) => (row.override ? { ...row, ...stripBlank(row.override), $id: row.$id } : row))
    .filter((row) => zoneMatches(row) && termMatches(row))
    .map((row) => ({
      ...row,
      // Marked so the lookup can say where a line came from. An assessor
      // choosing between "Rear Bumper" from the guide and one of the shop's own
      // lines should be able to see which is which.
      isOrgLine: true,
    }));
}

/** An override contributes only the fields it actually sets. */
function stripBlank(row) {
  const out = {};
  for (const [key, value] of Object.entries(row)) {
    if (value === null || value === undefined || value === '') continue;
    if (['$id', 'organisationId', 'standardLineId', 'active'].includes(key)) continue;
    out[key] = value;
  }
  return out;
}

// =====================================================
// DASHBOARD STATS
// =====================================================

/**
 * Get dashboard statistics.
 *
 * Returns a count per status rather than the old pending/completed pair: the
 * dashboard shows the quoting pipeline, and every tile on it has to lead
 * somewhere real. There is no revenue figure here — the one that used to be
 * returned was a hardcoded zero feeding a "Monthly Revenue" tile, which is
 * worse than no tile at all.
 */
export async function dashboardStats() {
  const quoteRequests = await quoteRequestList({});

  // quoteRequestList already orders newest first; slice a copy so the array it
  // returned is never re-ordered underneath the caller.
  const recentRequests = quoteRequests
    .slice()
    .sort((a, b) => new Date(b.$createdAt) - new Date(a.$createdAt))
    .slice(0, 5);

  return {
    totalQuoteRequests: quoteRequests.length,
    byStatus: countByStatus(quoteRequests),
    recentRequests
  };
}

// =====================================================
// CUSTOMER ORGANISATIONS
// =====================================================

/**
 * Create customer organisation
 */
export async function customerOrganisationCreate(data) {
  return await customerCreate({
    ...data,
    type: 'organisation'
  });
}

// =====================================================
// LICENCE & BILLING
// =====================================================
// Entitlements are READ straight from the database: `org_licences` grants read
// to the owning team and write to nobody, and `plans` is public lookup data
// like regions and insurers. That keeps the sidebar meter and the Generate
// button off the critical path of a function cold start.
//
// Treat all of it as display data. The limit is enforced server-side in the
// assessment orchestrator against the same row, so a client that lies to
// itself changes what it shows and nothing about what it can do.
//
// Only the things that genuinely need a server go through the `licence`
// function: the Lemon Squeezy API key, and the billing identifiers held in
// `org_billing_accounts`, which no client may read.

const SYSTEM_DATABASE_ID = 'flickquote-system';
const LICENCES_COLLECTION = 'org_licences';
const PLANS_COLLECTION = 'plans';

/** Calendar-month usage window (UTC) — matches the server-side guard. */
function currentUsagePeriodKey(now = new Date()) {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** First instant of next month — when the allowance resets. */
function usagePeriodResetsAt(now = new Date()) {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)).toISOString();
}

/**
 * The plan catalogue. Public lookup data, so this is a plain list query.
 */
export async function planList() {
  const response = await databases.listDocuments(
    DATABASE_ID,
    PLANS_COLLECTION,
    [Query.equal('active', true), Query.orderAsc('sortOrder'), Query.limit(50)]
  );

  return response.documents.map(plan => ({
    id: plan.$id,
    name: plan.name,
    tagline: plan.tagline || '',
    priceLabel: plan.priceLabel || '',
    priceAmount: plan.priceAmount ?? 0,
    priceCurrency: plan.priceCurrency || 'NZD',
    interval: plan.interval || null,
    aiAssessmentLimit: plan.aiAssessmentLimit,
    features: plan.features || [],
    purchasable: !!plan.lemonVariantId
  }));
}

/**
 * Entitlement summary for the current organisation: one point read on the
 * licence row, joined against the plan catalogue for display copy.
 *
 * Returns null when the organisation has no licence row yet (created before
 * licensing existed) — the caller can ask the `licence` function to backfill
 * one, which is the only path that writes here.
 */
export async function licenceSummary() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const [licence, catalogue] = await Promise.all([
    databases.getDocument(SYSTEM_DATABASE_ID, LICENCES_COLLECTION, user.teamId).catch(err => {
      if (err.code === 404) return null;
      throw err;
    }),
    planList().catch(() => [])
  ]);

  if (!licence) return null;

  const plan = catalogue.find(p => p.id === licence.planId) || null;
  const freePlan = catalogue.find(p => p.id === 'free');

  // Mirrors resolveAiAssessmentLimit in the functions: an override wins, a
  // lapsed subscription falls back to the free allowance rather than to zero.
  const ENTITLED = ['active', 'trialing', 'cancelled', 'past_due'];
  const freeLimit = freePlan?.aiAssessmentLimit ?? 0;
  let limit;
  if (typeof licence.overrideAiAssessmentLimit === 'number') {
    limit = licence.overrideAiAssessmentLimit;
  } else if (!ENTITLED.includes(licence.status || 'active')) {
    limit = freeLimit;
  } else {
    limit = typeof licence.aiAssessmentLimit === 'number' ? licence.aiAssessmentLimit : freeLimit;
  }

  // The counter on the row is written by the server after each run. A stale
  // period key means nothing has been used since the month rolled over.
  const periodKey = currentUsagePeriodKey();
  const used = licence.usagePeriodKey === periodKey ? (licence.aiAssessmentsUsed || 0) : 0;
  const unlimited = limit === -1;

  return {
    organisationId: user.teamId,
    planId: licence.planId,
    planName: plan?.name || licence.planId,
    status: licence.status || 'active',
    renewsAt: licence.renewsAt || null,
    cancelsAt: licence.endsAt || null,
    trialEndsAt: licence.trialEndsAt || null,
    usage: {
      periodKey,
      used,
      limit,
      unlimited,
      remaining: unlimited ? null : Math.max(0, limit - used),
      exhausted: !unlimited && used >= limit,
      resetsAt: usagePeriodResetsAt()
    },
    plan,
    catalogue
  };
}

/**
 * Backfill a free licence for an organisation that predates licensing.
 * Only ever creates the free tier, so it is safe for any member to call.
 */
export async function licenceProvision() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const result = await appwrite.executeFunction(
    FUNCTION_IDS.LICENCE,
    JSON.stringify({ action: 'provision', organisationId: user.teamId }),
    '/provision',
    'POST'
  );
  return result?.data || null;
}

/**
 * Full billing picture — subscription state, card summary, invoice history and
 * a freshly minted Lemon Squeezy customer portal link.
 * Admins only; the function returns 403 for everyone else.
 */
export async function licenceBilling() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const result = await appwrite.executeFunction(
    FUNCTION_IDS.LICENCE,
    JSON.stringify({ action: 'billing', organisationId: user.teamId }),
    '/billing',
    'POST'
  );
  return result?.data || null;
}

/**
 * Start an upgrade. Returns a Lemon Squeezy checkout URL that is already bound
 * server-side to this organisation — the plan id is the only thing we send.
 */
export async function licenceCheckout(planId) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const result = await appwrite.executeFunction(
    FUNCTION_IDS.LICENCE,
    JSON.stringify({ action: 'checkout', organisationId: user.teamId, planId }),
    '/checkout',
    'POST'
  );
  return result?.data?.url || null;
}

/**
 * Open the Lemon Squeezy customer portal, where invoices, payment method and
 * cancellation live. The URL is signed and short-lived so it is always minted
 * on demand rather than cached.
 */
export async function licencePortal() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const result = await appwrite.executeFunction(
    FUNCTION_IDS.LICENCE,
    JSON.stringify({ action: 'portal', organisationId: user.teamId }),
    '/portal',
    'POST'
  );
  return result?.data?.url || null;
}

// =====================================================
// SIGNUP
// =====================================================

/**
 * What the signup flow should do next for the freshly authenticated user:
 * ask for a shop name, or send them into an organisation they already have.
 */
export async function signupStatus() {
  const result = await appwrite.executeFunction(
    FUNCTION_IDS.ORG_SIGNUP,
    JSON.stringify({ action: 'status' }),
    '/status',
    'POST'
  );
  return result?.data || null;
}

/**
 * Create the organisation. One call provisions the team, makes the caller its
 * owner and admin, and starts them on the free plan.
 */
export async function signupCreateOrganisation(organisationName, fullName) {
  const result = await appwrite.executeFunction(
    FUNCTION_IDS.ORG_SIGNUP,
    JSON.stringify({ action: 'create', organisationName, fullName }),
    '/create',
    'POST'
  );
  return result?.data || null;
}

// =====================================================
// SUPPORT REQUESTS
// =====================================================
//
// Tickets live in the internal-tools database alongside PanelGear HQ's own desk, not in the
// product database — a request raised here is the same row HQ triages, and there is no second
// ticket system. What keeps one shop out of another's tickets is row permissions, not a
// query: `tickets` and `ticket_messages` carry rowSecurity, grant `create` to users and grant
// read to nobody at table level, so a row is visible only to the team named on it.
//
// That is why every write below passes `[Permission.read(Role.team(teamId))]` and nothing
// else. No update grant means a customer cannot rewrite their own ticket's status once it is
// filed — reopening happens by replying, which `support-message-hooks` acts on.

/** The internal-tools database. A fixed id, not configuration — see .github/conventions.md. */
const INTERNAL_DATABASE_ID = 'panelgear-internal';
const TICKETS_COLLECTION = 'tickets';
const TICKET_MESSAGES_COLLECTION = 'ticket_messages';
const SUPPORT_ATTACHMENTS_BUCKET = 'support-attachments';

/** Request types, as the dialog offers them. `idea` is what HQ already calls a suggestion. */
export const SUPPORT_REQUEST_TYPES = [
  { value: 'support', label: 'Support' },
  { value: 'idea', label: 'Suggestion' },
];

/** The only grant a support row ever carries. Read, for the shop that raised it. */
const supportGrant = (teamId) => [Permission.read(Role.team(teamId))];

/**
 * File a support request: the attachments, then the ticket, then its first message.
 *
 * Not a transaction, and deliberately ordered worst-loss-last. An attachment that uploads
 * before a ticket that fails is an orphaned file; a ticket that exists without its message
 * would be a blank request nobody can action.
 *
 * @param {{ type?: string, message: string, subject?: string, files?: File[] }} input
 */
export async function submitSupportRequest({ type = 'support', message, subject, files = [] }) {
  const body = String(message || '').trim();
  if (!body) throw new Error('A message is required');

  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No organisation selected');
  const permissions = supportGrant(user.teamId);

  const attachmentFileIds = [];
  for (const file of files) {
    const stored = await storage.createFile(
      SUPPORT_ATTACHMENTS_BUCKET, ID.unique(), file, permissions,
    );
    attachmentFileIds.push(stored.$id);
  }

  // The first line doubles as the subject when the user has not been asked for one, which is
  // the whole point of a one-box form. Trimmed to the column's 500 characters.
  const derivedSubject = String(subject || body.split('\n')[0] || 'Support request')
    .trim().slice(0, 500);

  const ticket = await withReference((reference) => databases.createDocument(
    INTERNAL_DATABASE_ID,
    TICKETS_COLLECTION,
    ID.unique(),
    {
      reference,
      subject: derivedSubject,
      status: 'new',
      type,
      channel: 'admin_app',
      requesterEmail: user.email,
      requesterName: user.name || null,
      requesterType: 'staff',
      requesterUserId: user.id,
      organisationId: user.teamId,
      lastMessageAt: new Date().toISOString(),
    },
    permissions,
  ));

  await databases.createDocument(
    INTERNAL_DATABASE_ID,
    TICKET_MESSAGES_COLLECTION,
    ID.unique(),
    {
      ticketId: ticket.$id,
      direction: 'inbound',
      fromEmail: user.email,
      fromName: user.name || null,
      subject: derivedSubject,
      bodyText: body.slice(0, 65535),
      attachmentFileIds,
      authorUserId: user.id,
    },
    permissions,
  );

  return { ticketId: ticket.$id, reference: ticket.reference };
}

/**
 * Every request raised by anyone in the signed-in user's organisation.
 *
 * The `organisationId` filter is for ordering and clarity, not access control — permissions
 * already make another organisation's rows unreadable. Both are wanted: a query that returned
 * rows the caller cannot read would page through holes.
 */
export async function listMySupportRequests(limit = 100) {
  const user = await getCurrentUser();
  if (!user?.teamId) return [];

  const response = await databases.listDocuments(
    INTERNAL_DATABASE_ID,
    TICKETS_COLLECTION,
    [
      Query.equal('organisationId', user.teamId),
      Query.orderDesc('$createdAt'),
      Query.limit(limit),
    ],
  );
  return response.documents;
}

/** One request, by id. A 401 here means it belongs to another organisation. */
export async function getSupportRequest(ticketId) {
  return databases.getDocument(INTERNAL_DATABASE_ID, TICKETS_COLLECTION, ticketId);
}

/**
 * The customer-visible thread.
 *
 * Internal notes are absent because HQ writes them with no row permissions at all, so they
 * are unreadable here rather than filtered out — see apps/internal/src/api/tickets.ts.
 */
export async function listSupportMessages(ticketId) {
  const response = await databases.listDocuments(
    INTERNAL_DATABASE_ID,
    TICKET_MESSAGES_COLLECTION,
    [
      Query.equal('ticketId', ticketId),
      Query.orderAsc('$createdAt'),
      Query.limit(200),
    ],
  );
  return response.documents;
}

/**
 * Reply on an existing request.
 *
 * `lastMessageAt` and reopening a resolved ticket are left to `support-message-hooks`, which
 * fires on this row's creation. The customer has no update grant on the ticket, so it could
 * not do either from here even if it wanted to.
 */
export async function addSupportMessage(ticketId, message, files = []) {
  const body = String(message || '').trim();
  if (!body) throw new Error('A message is required');

  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No organisation selected');
  const permissions = supportGrant(user.teamId);

  const attachmentFileIds = [];
  for (const file of files) {
    const stored = await storage.createFile(
      SUPPORT_ATTACHMENTS_BUCKET, ID.unique(), file, permissions,
    );
    attachmentFileIds.push(stored.$id);
  }

  return databases.createDocument(
    INTERNAL_DATABASE_ID,
    TICKET_MESSAGES_COLLECTION,
    ID.unique(),
    {
      ticketId,
      direction: 'inbound',
      fromEmail: user.email,
      fromName: user.name || null,
      bodyText: body.slice(0, 65535),
      attachmentFileIds,
      authorUserId: user.id,
    },
    permissions,
  );
}

/** A view URL for an attachment. Readable only by the organisation the file was granted to. */
export function getSupportAttachmentUrl(fileId) {
  return storage.getFileView(SUPPORT_ATTACHMENTS_BUCKET, fileId);
}

// =====================================================
// EXPORTED API OBJECT (for backward compatibility)
// =====================================================

export const api = {
  // Quote Requests
  quoteRequestList,
  quoteRequestGet,
  quoteRequestCreate,
  quoteRequestUpdate,
  quoteRequestLinkCustomer,
  quoteRequestLinkOrganisation,
  quoteRequestDelete,
  quoteRequestImages,
  quoteRequestImageGet,
  uploadQuoteImage,
  updateQuoteRequestImage,
  deleteQuoteRequestImage,
  saveQuoteRequestImagePositions,
  getImageUrl,
  getImageFileUrl,
  
  // Customers
  customerList,
  customerGet,
  customerCreate,
  customerUpdate,
  customerVehicles,
  customerQuoteRequests,
  customerOrganisationCreate,
  
  // Vehicles
  vehicleList,
  vehicleGet,
  vehicleSearchByRego,
  vehicleCreate,
  vehicleUpdate,
  vehicleQuoteRequests,
  
  // Quotes
  quoteList,
  getOrCreateQuote,
  quoteCreate,
  quoteUpdate,
  quoteLineUpdate,
  
  // Users/Team Members
  userList,
  userGetCurrent,
  userCreate,
  userProfileList,
  userProfileUpdate,
  userProfilesBackfill,
  userResendInvite,
  userProfileMarkAccepted,
  userUpdateLabels,
  userUpdateRoles, // alias for userUpdateLabels
  membershipUpdateRoles, // Update team membership roles
  userUpdateName,
  userUpdatePhone,
  userUpdateStatus,
  userRemove,
  
  // Organisations/Teams
  organisationGet,
  organisationUpdate,
  organisationGetCustomers,
  
  // Organisation Settings
  organisationSettingsGet,
  organisationSettingsUpdate,
  organisationSettingsCreate,

  // Customer intake form
  intakeSettingsGet,
  intakeSettingsSave,
  newIntakeService,
  
  // Panel Quote Integration
  panelQuoteSettingsGet,
  panelQuoteSettingsSave,
  panelQuoteTestConnection,
  panelQuoteCachedQuotes,
  panelQuoteSyncStatusGet,
  panelQuoteSyncQuotes,
  panelQuoteImport,
  panelQuoteImportBatch,
  panelQuoteCreateJob,
  panelQuoteSyncCustomers,
  panelQuoteCachedCustomers,
  panelQuoteInsurerMappings,
  panelQuoteAutoMapInsurers,
  panelQuoteSaveInsurerMapping,
  panelQuoteDeleteInsurerMapping,
  
  // Licence & Billing
  planList,
  licenceSummary,
  licenceProvision,
  licenceBilling,
  licenceCheckout,
  licencePortal,

  // Signup
  signupStatus,
  signupCreateOrganisation,

  // Damage Assessment
  generateDamageAssessment,

  // Quote Generation
  createGeneratingPlaceholder,
  generateQuoteFromDamage,
  
  // Lookup Data
  regionList,
  insurerList,
  insurerConfigGet,
  insurerGuidelines,
  makesModelsList,
  getInsurerParts,
  
  // Dashboard
  dashboardStats,

  // Support requests
  submitSupportRequest,
  listMySupportRequests,
  getSupportRequest,
  listSupportMessages,
  addSupportMessage,
  getSupportAttachmentUrl,

  // Shop line sheet
  shopLineList,
  shopLineCreate,
  shopLineCreateMany,
  shopLineOverride,
  shopLineUpdate,
  shopLineSetActive,
  shopLineResetToStandard,
  shopLineDelete,
  clearShopLineCache,
  clearInsurerPartsCache
};

// Default export for backward compatibility with `import { dataApi } from './dataApi'`
export const dataApi = api;

export default api;
